import React, { useState, useEffect } from 'react';
import {
  CurrencyRupeeIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  EyeIcon,
  ReceiptRefundIcon
} from '@heroicons/react/24/outline';
import adminApi from "../../api/adminApi.js";
import Toast from '../../components/ui/Toast.jsx';

const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const showToast = (msg, type = 'success') => setNotification({ msg, type });

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const data = await adminApi.getAllPayments();
      setPayments(data || []);
      setFilteredPayments(data || []);
    } catch (error) {
      showToast("Failed to load real-time transactions", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPayments(); }, []);

  useEffect(() => {
    let filtered = [...payments];

    // 1. Search Filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.user_name?.toLowerCase().includes(lowerSearch) ||
        p.user_email?.toLowerCase().includes(lowerSearch) ||
        p.razorpay_payment_id?.toLowerCase().includes(lowerSearch) ||
        p.razorpay_order_id?.toLowerCase().includes(lowerSearch)
      );
    }

    // 2. Status Filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(p => p.status?.toLowerCase() === selectedStatus.toLowerCase());
    }

    // 3. Date Range Filter
    if (dateRange.start) {
      const start = new Date(dateRange.start).setHours(0, 0, 0, 0);
      filtered = filtered.filter(p => new Date(p.created_at) >= start);
    }
    if (dateRange.end) {
      const end = new Date(dateRange.end).setHours(23, 59, 59, 999);
      filtered = filtered.filter(p => new Date(p.created_at) <= end);
    }

    setFilteredPayments(filtered);
  }, [payments, searchTerm, selectedStatus, dateRange]);

  const stats = (() => {
    const successful = payments.filter(p => ['captured', 'completed', 'success'].includes(p.status?.toLowerCase()));
    return {
      totalRevenue: successful.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0),
      totalCount: payments.length,
      pendingCount: payments.filter(p => p.status?.toLowerCase() === 'pending').length,
      successCount: successful.length
    };
  })();

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': case 'captured': case 'success': return 'bg-green-900/30 text-green-300 border-green-700/30';
      case 'pending': return 'bg-yellow-900/30 text-yellow-300 border-yellow-700/30';
      case 'failed': return 'bg-red-900/30 text-red-300 border-red-700/30';
      case 'refunded': return 'bg-blue-900/30 text-blue-300 border-blue-700/30';
      default: return 'bg-gray-700/50 text-gray-300 border-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': case 'captured': case 'success': return <CheckCircleIcon className="h-4 w-4" />;
      case 'pending': return <ClockIcon className="h-4 w-4" />;
      case 'failed': return <XCircleIcon className="h-4 w-4" />;
      case 'refunded': return <ReceiptRefundIcon className="h-4 w-4" />;
      default: return null;
    }
  };

  const exportToCSV = () => {
    const headers = ['Payment ID', 'User', 'Email', 'Competition', 'Amount', 'Status', 'Date', 'Method'];
    const csvData = filteredPayments.map(p => [
      p.razorpay_payment_id || p.razorpay_order_id,
      p.user_name, p.user_email, p.competition_title,
      p.amount, p.status, new Date(p.created_at).toLocaleDateString('en-IN'),
      p.payment_method
    ]);
    const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payments_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4">
      {notification && <Toast message={notification.msg} type={notification.type} onClose={() => setNotification(null)} />}

      <div className="bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center">
              <CurrencyRupeeIcon className="h-7 w-7 mr-3 text-green-400" /> Payment Management
            </h1>
            <p className="text-gray-400 mt-1">Track and manage all platform revenue</p>
          </div>
          <button onClick={exportToCSV} className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500">
            <ArrowDownTrayIcon className="h-5 w-5" /> <span>Export CSV</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5">
          <p className="text-sm text-gray-400">Total Revenue</p>
          <p className="text-2xl font-bold text-white">₹{stats.totalRevenue.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5">
          <p className="text-sm text-gray-400">Success</p>
          <p className="text-2xl font-bold text-green-400">{stats.successCount}</p>
        </div>
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5">
          <p className="text-sm text-gray-400">Pending</p>
          <p className="text-2xl font-bold text-yellow-400">{stats.pendingCount}</p>
        </div>
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5">
          <p className="text-sm text-gray-400">Total Transactions</p>
          <p className="text-2xl font-bold text-blue-400">{stats.totalCount}</p>
        </div>
      </div>

      <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 flex flex-wrap gap-4">
        <div className="flex-1 min-w-[300px] relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
          <input 
            type="text" placeholder="Search user, email or ID..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select 
          value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}
          className="bg-gray-900 border border-gray-700 rounded-lg text-white px-4 py-2 outline-none"
        >
          <option value="all">All Statuses</option>
          <option value="captured">Captured</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
          <option value="refunded">Refunded</option>
        </select>
      </div>

      <div className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900/50 text-gray-400 text-xs uppercase">
              <tr>
                <th className="px-6 py-4 text-left">Details</th>
                <th className="px-6 py-4 text-left">User</th>
                <th className="px-6 py-4 text-left">Competition</th>
                <th className="px-6 py-4 text-left">Amount</th>
                <th className="px-6 py-4 text-left">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr><td colSpan="6" className="py-10 text-center text-gray-400"><ArrowPathIcon className="h-8 w-8 animate-spin mx-auto mb-2"/>Loading...</td></tr>
              ) : filteredPayments.length === 0 ? (
                <tr><td colSpan="6" className="py-10 text-center text-gray-500 italic">No transactions found</td></tr>
              ) : (
                filteredPayments.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-700/20 transition-colors">
                    <td className="px-6 py-4">
                      {/* Logic: If payment is not captured yet, show Order ID */}
    <div className="font-mono text-xs text-blue-400">
      {p.razorpay_payment_id ? p.razorpay_payment_id : `Order: ${p.razorpay_order_id.substring(0, 12)}...`}
    </div>
    <div className="text-[10px] text-gray-500 uppercase mt-1">
      {p.payment_method || 'Processing...'}
    </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-white">{p.user_name || 'Unknown User'}</div>
    <div className="text-xs text-gray-500">{p.user_email || 'No Email'}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">{p.competition_title || 'General Payment'}</td>
                    <td className="px-6 py-4 font-bold text-white">₹{p.amount}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(p.status)}`}>
                        {getStatusIcon(p.status)} <span className="ml-1 capitalize">{p.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button className="p-1.5 text-gray-400 hover:text-white transition-colors"><EyeIcon className="h-5 w-5" /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPayments;